// @ts-nocheck
/**
 * Carbon UI Shell Component
 * Fully compliant Carbon Design System navigation implementation
 * Provides responsive navigation with proper accessibility support
 */

import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  HeaderContainer,
  Header,
  HeaderGlobalBar,
  HeaderGlobalAction,
  HeaderMenuButton,
  HeaderName,
  HeaderNavigation,
  HeaderMenuItem,
  SideNav,
  SideNavItems,
  SideNavMenuItem,
  SideNavMenu,
  SideNavLink,
  SideNavDivider,
  Content,
  Theme,
  HeaderPanel,
  Switcher,
  SwitcherItem,
  SwitcherDivider,
  SkipToContent,
} from '@carbon/react';
import {
  Dashboard,
  UserMultiple,
  Settings,
  User,
  Logout,
  Notification,
  Analytics,
  Education,
  ChartLine,
  Bot,
  Trophy,
  ChartBar,
  Add,
  TestTool,
  CloudFoundry_1,
  UserAvatar,
  Light,
  Asleep,
  Security,
  View,
  Download,
  TrashCan,
  Document,
  Email,
  Share,
} from '@carbon/icons-react';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/authService';
import { useTheme } from '../../contexts/ThemeContext';
import ThemeSwitcher from '../theme/ThemeSwitcher';

interface CarbonUIShellProps {
  children?: React.ReactNode;
}

let shellRenderCount = 0;

const CarbonUIShell: React.FC<CarbonUIShellProps> = ({ children }) => {
  const renderNumber = ++shellRenderCount;
  const [isSideNavExpanded, setIsSideNavExpanded] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isUserPanelOpen, setIsUserPanelOpen] = useState(false);
  const [isThemePanelOpen, setIsThemePanelOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { theme } = useTheme();

  console.log(`[CarbonUIShell] Render #${renderNumber}`, {
    pathname: location.pathname,
    userRole: user?.role,
    theme,
    isSideNavExpanded,
    isNotificationPanelOpen,
    isUserPanelOpen,
    isThemePanelOpen,
  });

  // Track mount/unmount
  React.useEffect(() => {
    console.log(`[CarbonUIShell #${renderNumber}] MOUNTED`);
    return () => {
      console.log(`[CarbonUIShell #${renderNumber}] UNMOUNTED`);
    };
  }, []);

  // Helper functions
  const getRoleDisplayName = (role: string) => {
    const roles: Record<string, string> = {
      parent: 'Parent',
      student: 'Student',
      teacher: 'Teacher',
      admin: 'Administrator',
      creator: 'Individual Adult Learner',
      individual: 'Individual Adult Learner',
    };
    return roles[role] || role;
  };

  const handleNavigation = (route: string) => {
    navigate(route);
    setIsSideNavExpanded(false);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      logout();
      navigate('/login');
    }
  };

  const isActiveRoute = (route: string) => {
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  // Dynamic header navigation based on role
  const getHeaderNavigation = () => {
    switch (user?.role) {
      case 'parent':
        return (
          <>
            <HeaderMenuItem isActive={isActiveRoute('/dashboard')} onClick={() => handleNavigation('/dashboard')}>
              Family Overview
            </HeaderMenuItem>
            <HeaderMenuItem isActive={isActiveRoute('/students')} onClick={() => handleNavigation('/students')}>
              My Children
            </HeaderMenuItem>
            <HeaderMenuItem isActive={isActiveRoute('/progress')} onClick={() => handleNavigation('/progress')}>
              Progress
            </HeaderMenuItem>
          </>
        );
      case 'student':
        return (
          <>
            <HeaderMenuItem isActive={isActiveRoute('/dashboard')} onClick={() => handleNavigation('/dashboard')}>
              Dashboard
            </HeaderMenuItem>
            <HeaderMenuItem isActive={isActiveRoute('/tutoring')} onClick={() => handleNavigation('/tutoring')}>
              AI Tutoring
            </HeaderMenuItem>
            <HeaderMenuItem isActive={isActiveRoute('/progress')} onClick={() => handleNavigation('/progress')}>
              My Progress
            </HeaderMenuItem>
          </>
        );
      case 'teacher':
        return (
          <>
            <HeaderMenuItem isActive={isActiveRoute('/dashboard')} onClick={() => handleNavigation('/dashboard')}>
              Dashboard
            </HeaderMenuItem>
            <HeaderMenuItem isActive={isActiveRoute('/classes')} onClick={() => handleNavigation('/classes')}>
              Classes
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/assignments')}
              onClick={() => handleNavigation('/teacher/assignments')}
            >
              Assignments
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/gradebook')}
              onClick={() => handleNavigation('/teacher/gradebook')}
            >
              Gradebook
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/content-library')}
              onClick={() => handleNavigation('/teacher/content-library')}
            >
              Content Library
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/communications')}
              onClick={() => handleNavigation('/teacher/communications')}
            >
              Communications
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/resources')}
              onClick={() => handleNavigation('/teacher/resources')}
            >
              Resources
            </HeaderMenuItem>
          </>
        );
      case 'admin':
        return (
          <>
            <HeaderMenuItem isActive={isActiveRoute('/dashboard')} onClick={() => handleNavigation('/dashboard')}>
              Dashboard
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/admin/marketing')}
              onClick={() => handleNavigation('/admin/marketing')}
            >
              Marketing
            </HeaderMenuItem>
            <HeaderMenuItem isActive={isActiveRoute('/admin/users')} onClick={() => handleNavigation('/admin/users')}>
              Users
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/content') || isActiveRoute('/assignments') || isActiveRoute('/gradebook')}
              onClick={() => handleNavigation('/content')}
            >
              Teaching Tools
            </HeaderMenuItem>
          </>
        );
      case 'creator':
      case 'individual':
        return (
          <>
            <HeaderMenuItem isActive={isActiveRoute('/dashboard')} onClick={() => handleNavigation('/dashboard')}>
              Dashboard
            </HeaderMenuItem>
            <HeaderMenuItem
              isActive={isActiveRoute('/individual-learning')}
              onClick={() => handleNavigation('/individual-learning')}
            >
              Self-Paced Learning
            </HeaderMenuItem>
            <HeaderMenuItem isActive={isActiveRoute('/pathways')} onClick={() => handleNavigation('/pathways')}>
              Learning Pathways
            </HeaderMenuItem>
          </>
        );
      default:
        return null;
    }
  };

  // Dynamic side navigation based on role
  const getSideNavigation = () => {
    const commonItems = (
      <SideNavMenuItem isActive={isActiveRoute('/dashboard')} onClick={() => handleNavigation('/dashboard')}>
        <Dashboard size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
        Dashboard
      </SideNavMenuItem>
    );

    switch (user?.role) {
      case 'parent':
        return (
          <>
            {commonItems}
            <SideNavMenu
              title="My Children"
              isActive={location.pathname.startsWith('/students')}
              renderIcon={UserMultiple}
            >
              {user.children?.map(child => (
                <SideNavMenuItem
                  key={child.student_id}
                  onClick={() => handleNavigation(`/students/${child.student_id}`)}
                  isActive={isActiveRoute(`/students/${child.student_id}`)}
                >
                  Grade {child.grade_level} Student
                </SideNavMenuItem>
              )) || []}
            </SideNavMenu>
            <SideNavMenuItem isActive={isActiveRoute('/progress')} onClick={() => handleNavigation('/progress')}>
              <ChartLine size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Progress Overview
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/family/settings')}
              onClick={() => handleNavigation('/family/settings')}
            >
              <Settings size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Family Settings
            </SideNavMenuItem>
            <SideNavDivider />
            <SideNavMenu
              title="Privacy & Security"
              isActive={location.pathname.startsWith('/privacy')}
              renderIcon={Security}
            >
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/dashboard')}
                isActive={isActiveRoute('/privacy/dashboard')}
              >
                Privacy Dashboard
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/consent')}
                isActive={isActiveRoute('/privacy/consent')}
              >
                Consent Management
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/audit')}
                isActive={isActiveRoute('/privacy/audit')}
              >
                Audit Log
              </SideNavMenuItem>
            </SideNavMenu>
          </>
        );

      case 'student':
        return (
          <>
            {commonItems}
            <SideNavMenuItem isActive={isActiveRoute('/tutoring')} onClick={() => handleNavigation('/tutoring')}>
              <Bot size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              AI Tutoring
            </SideNavMenuItem>
            <SideNavMenuItem isActive={isActiveRoute('/progress')} onClick={() => handleNavigation('/progress')}>
              <ChartLine size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              My Progress
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/achievements')}
              onClick={() => handleNavigation('/achievements')}
            >
              <Trophy size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Achievements
            </SideNavMenuItem>
            <SideNavDivider />
            <SideNavMenu
              title="Privacy & Security"
              isActive={location.pathname.startsWith('/privacy')}
              renderIcon={Security}
            >
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/dashboard')}
                isActive={isActiveRoute('/privacy/dashboard')}
              >
                Privacy Dashboard
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/consent')}
                isActive={isActiveRoute('/privacy/consent')}
              >
                Consent Management
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/audit')}
                isActive={isActiveRoute('/privacy/audit')}
              >
                Audit Log
              </SideNavMenuItem>
            </SideNavMenu>
          </>
        );

      case 'teacher':
        return (
          <>
            {commonItems}
            <SideNavMenuItem isActive={isActiveRoute('/classes')} onClick={() => handleNavigation('/classes')}>
              <UserMultiple size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Classes
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/assignments')}
              onClick={() => handleNavigation('/teacher/assignments')}
            >
              <Education size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Assignments
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/gradebook')}
              onClick={() => handleNavigation('/teacher/gradebook')}
            >
              <ChartLine size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Gradebook
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/content-library')}
              onClick={() => handleNavigation('/teacher/content-library')}
            >
              <Document size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Content Library
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/communications')}
              onClick={() => handleNavigation('/teacher/communications')}
            >
              <Email size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Communications
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/resources')}
              onClick={() => handleNavigation('/teacher/resources')}
            >
              <Share size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Resources
            </SideNavMenuItem>
            <SideNavMenuItem isActive={isActiveRoute('/students')} onClick={() => handleNavigation('/students')}>
              <UserMultiple size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Students
            </SideNavMenuItem>
            <SideNavDivider />
            <SideNavMenu
              title="Privacy & Security"
              isActive={location.pathname.startsWith('/privacy')}
              renderIcon={Security}
            >
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/dashboard')}
                isActive={isActiveRoute('/privacy/dashboard')}
              >
                Privacy Dashboard
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/consent')}
                isActive={isActiveRoute('/privacy/consent')}
              >
                Consent Management
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/audit')}
                isActive={isActiveRoute('/privacy/audit')}
              >
                Audit Log
              </SideNavMenuItem>
            </SideNavMenu>
          </>
        );

      case 'admin':
        return (
          <>
            {commonItems}
            <SideNavMenu
              title="Marketing Management"
              isActive={location.pathname.startsWith('/admin/marketing')}
              renderIcon={ChartBar}
            >
              <SideNavMenuItem
                onClick={() => handleNavigation('/admin/marketing')}
                isActive={isActiveRoute('/admin/marketing') && !location.search}
              >
                Content Portal
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/admin/marketing?tab=library')}
                isActive={location.search.includes('tab=library')}
              >
                Content Library
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/admin/marketing?tab=ab-testing')}
                isActive={location.search.includes('tab=ab-testing')}
              >
                A/B Testing
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/admin/marketing?tab=analytics')}
                isActive={location.search.includes('tab=analytics')}
              >
                Analytics
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/admin/marketing?tab=cache')}
                isActive={location.search.includes('tab=cache')}
              >
                Cache Management
              </SideNavMenuItem>
            </SideNavMenu>
            <SideNavMenuItem isActive={isActiveRoute('/admin/users')} onClick={() => handleNavigation('/admin/users')}>
              <UserMultiple size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              User Management
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/admin/settings')}
              onClick={() => handleNavigation('/admin/settings')}
            >
              <Settings size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              System Settings
            </SideNavMenuItem>
            <SideNavDivider />
            <SideNavMenu
              title="Teacher Management"
              isActive={
                location.pathname.startsWith('/content') ||
                location.pathname.startsWith('/assignments') ||
                location.pathname.startsWith('/gradebook')
              }
              renderIcon={Education}
            >
              <SideNavMenuItem onClick={() => handleNavigation('/content')} isActive={isActiveRoute('/content')}>
                Content Management
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/assignments')}
                isActive={isActiveRoute('/assignments')}
              >
                Assignment Builder
              </SideNavMenuItem>
              <SideNavMenuItem onClick={() => handleNavigation('/gradebook')} isActive={isActiveRoute('/gradebook')}>
                Grade Book
              </SideNavMenuItem>
              <SideNavMenuItem onClick={() => handleNavigation('/classes')} isActive={isActiveRoute('/classes')}>
                Class Management
              </SideNavMenuItem>
            </SideNavMenu>
            <SideNavDivider />
            <SideNavMenu
              title="Privacy & Compliance"
              isActive={location.pathname.startsWith('/privacy') || location.pathname.startsWith('/admin/compliance')}
              renderIcon={Security}
            >
              <SideNavMenuItem
                onClick={() => handleNavigation('/admin/compliance')}
                isActive={isActiveRoute('/admin/compliance')}
              >
                Compliance Dashboard
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/dashboard')}
                isActive={isActiveRoute('/privacy/dashboard')}
              >
                Privacy Dashboard
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/consent')}
                isActive={isActiveRoute('/privacy/consent')}
              >
                Consent Management
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/audit')}
                isActive={isActiveRoute('/privacy/audit')}
              >
                Audit Log
              </SideNavMenuItem>
            </SideNavMenu>
          </>
        );

      case 'creator':
      case 'individual':
        return (
          <>
            {commonItems}
            <SideNavMenuItem
              isActive={isActiveRoute('/individual-learning')}
              onClick={() => handleNavigation('/individual-learning')}
            >
              <Education size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Self-Paced Learning
            </SideNavMenuItem>
            <SideNavMenuItem isActive={isActiveRoute('/pathways')} onClick={() => handleNavigation('/pathways')}>
              <ChartLine size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Learning Pathways
            </SideNavMenuItem>
            <SideNavMenuItem isActive={isActiveRoute('/progress')} onClick={() => handleNavigation('/progress')}>
              <Analytics size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Progress Analytics
            </SideNavMenuItem>
            <SideNavMenuItem
              isActive={isActiveRoute('/achievements')}
              onClick={() => handleNavigation('/achievements')}
            >
              <Trophy size={16} style={{ marginRight: 'var(--cds-spacing-03)' }} />
              Achievements
            </SideNavMenuItem>
            <SideNavDivider />
            <SideNavMenu
              title="Privacy & Security"
              isActive={location.pathname.startsWith('/privacy')}
              renderIcon={Security}
            >
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/dashboard')}
                isActive={isActiveRoute('/privacy/dashboard')}
              >
                Privacy Dashboard
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/consent')}
                isActive={isActiveRoute('/privacy/consent')}
              >
                Consent Management
              </SideNavMenuItem>
              <SideNavMenuItem
                onClick={() => handleNavigation('/privacy/audit')}
                isActive={isActiveRoute('/privacy/audit')}
              >
                Audit Log
              </SideNavMenuItem>
            </SideNavMenu>
          </>
        );

      default:
        return commonItems;
    }
  };

  return (
    <HeaderContainer
      render={({ isSideNavExpanded: navExpanded, onClickSideNavExpand }) => (
        <>
          <Header aria-label="Sigma08 Learning Platform">
            <SkipToContent />
            <HeaderMenuButton
              aria-label={navExpanded ? 'Close menu' : 'Open menu'}
              onClick={() => {
                onClickSideNavExpand();
                setIsSideNavExpanded(!navExpanded);
              }}
              isActive={navExpanded}
            />

            <HeaderName
              href={
                user?.role === 'platform_admin' || user?.role === 'admin'
                  ? '/admin'
                  : user?.role === 'teacher'
                    ? '/teacher'
                    : user?.role === 'student'
                      ? '/student'
                      : user?.role === 'parent'
                        ? '/parent'
                        : '/dashboard'
              }
              prefix="Sigma08"
            >
              AI Learning Platform
            </HeaderName>

            <HeaderNavigation aria-label="Main navigation">{getHeaderNavigation()}</HeaderNavigation>

            <HeaderGlobalBar>
              {/* Admin quick actions */}
              {user?.role === 'platform_admin' && (
                <>
                  <HeaderGlobalAction
                    aria-label="Marketing Analytics - View analytics dashboard"
                    tooltipAlignment="end"
                    onClick={() => handleNavigation('/admin/marketing?tab=analytics')}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                  >
                    <Analytics size={20} />
                  </HeaderGlobalAction>
                  <HeaderGlobalAction
                    aria-label="Create Content - Add new marketing content"
                    tooltipAlignment="end"
                    onClick={() => handleNavigation('/admin/marketing?tab=library')}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                  >
                    <Add size={20} />
                  </HeaderGlobalAction>
                  <HeaderGlobalAction
                    aria-label="A/B Tests - Manage A/B testing campaigns"
                    tooltipAlignment="end"
                    onClick={() => handleNavigation('/admin/marketing?tab=ab-testing')}
                    style={{ minWidth: '44px', minHeight: '44px' }}
                  >
                    <TestTool size={20} />
                  </HeaderGlobalAction>
                </>
              )}

              <HeaderGlobalAction
                aria-label="Theme Settings - Switch between light and dark themes"
                tooltipAlignment="end"
                isActive={isThemePanelOpen}
                onClick={() => {
                  setIsThemePanelOpen(!isThemePanelOpen);
                  setIsNotificationPanelOpen(false);
                  setIsUserPanelOpen(false);
                }}
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                {theme === 'white' || theme === 'g10' ? <Light size={20} /> : <Asleep size={20} />}
              </HeaderGlobalAction>

              {/* Notifications */}
              <HeaderGlobalAction
                aria-label="Notifications - View your notifications and alerts"
                tooltipAlignment="end"
                isActive={isNotificationPanelOpen}
                onClick={() => {
                  setIsNotificationPanelOpen(!isNotificationPanelOpen);
                  setIsUserPanelOpen(false);
                  setIsThemePanelOpen(false);
                }}
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                <Notification size={20} />
              </HeaderGlobalAction>

              <HeaderGlobalAction
                aria-label="User settings - Access your profile and account settings"
                tooltipAlignment="end"
                isActive={isUserPanelOpen}
                onClick={() => {
                  setIsUserPanelOpen(!isUserPanelOpen);
                  setIsNotificationPanelOpen(false);
                  setIsThemePanelOpen(false);
                }}
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                <UserAvatar size={20} />
              </HeaderGlobalAction>
            </HeaderGlobalBar>

            {/* Notification Panel */}
            <HeaderPanel aria-label="Notifications panel" expanded={isNotificationPanelOpen}>
              <Switcher aria-label="Notifications">
                <SwitcherItem aria-label="No new notifications">
                  <div style={{ padding: 'var(--cds-spacing-05)' }}>
                    <p>No new notifications</p>
                  </div>
                </SwitcherItem>
              </Switcher>
            </HeaderPanel>

            {/* Theme Panel */}
            <HeaderPanel aria-label="Theme settings panel" expanded={isThemePanelOpen}>
              <Switcher aria-label="Theme selector">
                <SwitcherItem aria-label="Theme settings">
                  <div style={{ padding: 'var(--cds-spacing-05)' }}>
                    <ThemeSwitcher />
                  </div>
                </SwitcherItem>
              </Switcher>
            </HeaderPanel>

            {/* User Panel */}
            <HeaderPanel aria-label="User panel" expanded={isUserPanelOpen}>
              <Switcher aria-label="User menu">
                <SwitcherItem aria-label="User info" disabled>
                  <div
                    style={{
                      padding: 'var(--cds-spacing-04)',
                      borderBottom: '1px solid var(--cds-border-subtle)',
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{user?.family_name || user?.email || 'User'}</div>
                    <div
                      style={{
                        fontSize: 'var(--cds-body-short-01-font-size)',
                        color: 'var(--cds-text-secondary)',
                      }}
                    >
                      {getRoleDisplayName(user?.role || '')}
                    </div>
                  </div>
                </SwitcherItem>

                <SwitcherItem
                  aria-label="Profile"
                  onClick={() => {
                    handleNavigation('/profile');
                    setIsUserPanelOpen(false);
                  }}
                >
                  <User size={20} />
                  My Profile
                </SwitcherItem>

                {user?.role === 'parent' && (
                  <SwitcherItem
                    aria-label="Family Settings"
                    onClick={() => {
                      handleNavigation('/family/settings');
                      setIsUserPanelOpen(false);
                    }}
                  >
                    <Settings size={20} />
                    Family Settings
                  </SwitcherItem>
                )}

                <SwitcherItem
                  aria-label="Account Settings"
                  onClick={() => {
                    handleNavigation('/settings');
                    setIsUserPanelOpen(false);
                  }}
                >
                  <Settings size={20} />
                  Account Settings
                </SwitcherItem>

                <SwitcherDivider />

                <SwitcherItem
                  aria-label="Sign out"
                  onClick={() => {
                    handleLogout();
                    setIsUserPanelOpen(false);
                  }}
                >
                  <Logout size={20} />
                  Sign Out
                </SwitcherItem>
              </Switcher>
            </HeaderPanel>
          </Header>

          <SideNav
            aria-label="Side navigation"
            expanded={navExpanded || isSideNavExpanded}
            isPersistent={false}
            style={{
              backgroundColor: 'var(--cds-layer-01)',
              borderRight: '1px solid var(--cds-border-subtle)',
            }}
          >
            <SideNavItems>
              {getSideNavigation()}

              <SideNavDivider />

              <SideNavLink href="/help" target="_blank" rel="noopener noreferrer">
                Help & Support
              </SideNavLink>

              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: 'var(--cds-spacing-04)',
                  borderTop: '1px solid var(--cds-border-subtle)',
                  background: 'var(--cds-layer-02)',
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--cds-label-01-font-size)',
                    color: 'var(--cds-text-secondary)',
                    textAlign: 'center',
                  }}
                >
                  <div>FERPA & COPPA Compliant</div>
                  <div style={{ marginTop: 'var(--cds-spacing-02)' }}>
                    <Security size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Privacy Protected
                  </div>
                </div>
              </div>
            </SideNavItems>
          </SideNav>

          <Content>
            <div
              style={{
                padding: 'var(--cds-spacing-05)',
                minHeight: 'calc(100vh - 3rem)',
                backgroundColor: 'var(--cds-background)',
              }}
            >
              {children || <Outlet />}
            </div>
          </Content>
        </>
      )}
    />
  );
};

export default CarbonUIShell;
